/* ==========================================================================
   EZINK Cart Drawer
   Hand-built on the Shopify AJAX Cart API. No app dependency.
   --------------------------------------------------------------------------
   Verified against the EZINK store on 2026-08-08:
     - 4 Shopify Markets, one currency each: AUD (base), CAD, NZD, USD.
     - Free shipping at 65.00 of the customer's OWN presentment currency in
       every market, so a single minor-units constant is correct.
     - Every free-shipping rule tests TOTAL_PRICE, which is post-discount.
     - Recharge runs on Shopify Checkout Integration (native selling plans),
       so subscription and one-time carts both go to /checkout. No fork.
   ========================================================================== */

(function () {
  'use strict';

  /* ----------------------------------------------------------------------
     Constants
     ---------------------------------------------------------------------- */

  /* cart.total_price from /cart.js is ALREADY in the presentment currency's
     minor units — Shopify converts before it reaches us. Because all four
     markets are configured at 65 of their own currency (verified in the
     delivery profile, not assumed), one constant covers all of them.
     If a market's profile is ever retuned, this constant starts lying. */
  var FREE_SHIPPING_THRESHOLD = 6500;

  /* The standard shipping rate that a customer actually avoids by crossing
     the threshold, per market, in minor units. These are read from the live
     delivery profile — they are NOT one number, and quoting AUD 7.99 to a
     Canadian would understate their saving by two dollars. */
  var STANDARD_SHIPPING = {
    AUD: 799,
    CAD: 999,
    NZD: 899,
    USD: 799
  };

  var CHECKOUT_LABEL = 'Checkout';

  /* Shipping Protection. Seeded from Liquid so we get the variant id and the
     price in the customer's own presentment currency rather than hardcoding
     $2.99. This product used to be injected by UpCart; removing that app took
     it with it, so the drawer owns it now.
     Opt-in is ON by default, and the choice is remembered for the session so
     an explicit opt-out is never silently undone by re-opening the drawer. */
  var PROTECTION = window.__EZ_PROTECTION__ || null;
  var PROT_KEY = 'ez_protection_optin';

  /* The line-item property UploadKit writes the artwork URL to. Confirmed
     from the PDP widget, whose file input is literally named
     `properties[Uploaded file]`. If the merchant renames the field in the
     UploadKit admin, this must change with it. */
  var UPLOAD_PROP = 'Uploaded file';

  /* UploadKit lazy-loads the Uploadcare widget ONLY on pages that render an
     uploader field — i.e. product pages. Quick-add lives on collection pages,
     where window.uploadcare is undefined, so the drawer has to load the
     library itself. Version pinned to match what UploadKit loads on the PDP;
     verified 3.21.0 on 2026-08-08. UploadKit.config is present on every page,
     so the public key is always readable even when the widget is not. */
  /* The uploaded-file property points at UploadKit's download.html wrapper,
     NOT at the image, so it cannot be used as an <img src> directly. It does
     embed the Uploadcare UUID, and Uploadcare serves transformed derivatives
     straight off that UUID — verified loading at 200x200 on 2026-08-08.

     Building the thumbnail ourselves is what kills the flicker: the artwork
     is in the very first paint, so there is no URL-to-image swap to watch.
     It also works identically for files uploaded inside the drawer, which
     UploadKit's DOM scan never saw at all. */
  var UC_UUID_RE = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i;

  function artworkThumb(li) {
    var props = (li && li.properties) || {};
    for (var k in props) {
      if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
      if (k.charAt(0) === '_') continue;
      var v = String(props[k]);
      if (!/^https?:/i.test(v)) continue;

      /* UploadKit emits TWO different wrapper formats, and they must not be
         parsed the same way:

           legacy — one path segment, no query, and that single UUID IS the
                    Uploadcare file id.
           v2     — eight path segments and nine query params, containing
                    TWO different UUIDs. The file id is the `uu` param; the
                    other one is unrelated.

         Taking "the first UUID in the string" silently picks the wrong id in
         the v2 format. It still LOOKS right — a well-formed UUID, a
         well-formed derivative URL — but the derivative 404s, so the image
         errored and fell back to the product photo. That fallback swap was
         the flicker: the artwork appeared to load and then vanish.
         Verified 2026-08-08: `uu` resolves 200x200, first-in-string fails. */
      var id = null;
      try {
        var uu = new URL(v).searchParams.get('uu');
        if (uu) { var mu = uu.match(UC_UUID_RE); if (mu) id = mu[0]; }
      } catch (e) { /* not parseable as a URL — fall through to the regex */ }

      if (!id) {
        var m = v.match(UC_UUID_RE);
        if (m) id = m[0];
      }

      /* preview, NOT scale_crop. scale_crop fills the square by cutting the
         edges off, which on a tall portrait design removes most of it. These
         are the customer's own artwork — showing three quarters of it is
         worse than showing it small. preview fits the whole image inside the
         box (measured: 200x200 crop vs 114x200 and 122x200 preview on real
         uploads), and .ez-item__art below switches object-fit to match. */
      if (id) return 'https://ucarecdn.com/' + id + '/-/preview/200x200/-/format/auto/';
    }
    return null;
  }

  var UC_WIDGET_SRC = 'https://ucarecdn.com/libs/widget/3.21.0/uploadcare.full.min.js';

  function protOptIn() {
    try {
      var v = sessionStorage.getItem(PROT_KEY);
      return v === null ? true : v === '1';
    } catch (e) { return true; }
  }

  function setProtOptIn(on) {
    try { sessionStorage.setItem(PROT_KEY, on ? '1' : '0'); } catch (e) {}
  }

  function isProt(li) {
    return !!(PROTECTION && li && li.variant_id === PROTECTION.id);
  }

  /* ----------------------------------------------------------------------
     Money — ONE formatter, used by every number in the drawer.
     Never hardcode a symbol: the same "$" means four different things here.
     ---------------------------------------------------------------------- */

  var _nfCache = {};

  function formatMoney(cents, currency) {
    var code = currency || (window.EZCart && window.EZCart.currency) || 'AUD';
    var amount = (cents || 0) / 100;

    if (!_nfCache[code]) {
      var nf;
      try {
        // narrowSymbol gives "$12.99" rather than "A$12.99" / "US$12.99",
        // which matches how the rest of the theme renders money.
        nf = new Intl.NumberFormat('en', {
          style: 'currency',
          currency: code,
          currencyDisplay: 'narrowSymbol'
        });
      } catch (e) {
        // iOS Safari below 14.5 throws on narrowSymbol. Degrade, don't die.
        nf = new Intl.NumberFormat('en', { style: 'currency', currency: code });
      }
      _nfCache[code] = nf;
    }

    var out = _nfCache[code].format(amount);

    /* The theme has currency_code_enabled = true, so the storefront renders
       "$12.99 AUD" everywhere. The drawer matches, or it looks like a
       different site bolted on. */
    if (out.indexOf(code) === -1) out += ' ' + code;
    return out;
  }

  /* ----------------------------------------------------------------------
     Small helpers
     ---------------------------------------------------------------------- */

  function h(sel, root) {
    return (root || document).querySelector(sel);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function sized(url, w) {
    if (!url) return '';
    return url.replace(/(\.(?:png|jpe?g|webp|gif))(\?|$)/i, '_' + w + 'x$1$2');
  }

  /* ----------------------------------------------------------------------
     Controller
     ---------------------------------------------------------------------- */

  var EZCart = {
    cart: null,
    currency: 'AUD',
    el: null,
    built: false,
    lastFocus: null,
    recPool: [],
    recAnchor: null,

    /* compare-at prices, keyed "handle|CURRENCY".
       /cart.js does NOT carry compare_at_price — every price field on a line
       item is the sale price, which is why "was" pricing and the saving were
       both invisible. /products/<handle>.js does carry it, and is market
       aware: verified on 2026-08-08 that its `price` matches the cart line
       price exactly in AUD/USD/CAD/NZD, so the two endpoints can be paired
       without risk of mixing currencies. Cache key includes the currency so
       a market switch can never serve a stale conversion. */
    priceBook: {},

    /* Bundle parent products, keyed by handle -> {title, image, url}.
       Seeded from Liquid for any bundle already in the cart at page render,
       so the grouped row is correct on first paint; fetched on demand for
       bundles added afterwards. */
    productMeta: window.__EZ_BUNDLE_PARENTS__ || {},

    /* Every in-flight /cart/add.js call lives here. Checkout waits on these
       before navigating — rendering the button early is only safe if tapping
       it cannot outrun the cart it is meant to check out. */
    pendingAdds: new Set(),

    /* ---------------------------------------------------------------- init */

    init: function () {
      this.el = document.querySelector('ez-cart-drawer');
      if (!this.el) return;

      var seed = window.__EZ_CART_SEED__;
      if (seed) {
        this.cart = seed;
        this.currency = seed.currency || 'AUD';
      }

      this.cacheNodes();
      this.bind();

      /* Render straight from the Liquid seed. The drawer knows the cart
         before any network call, so opening it never waits on /cart.js. */
      if (this.cart) this.render();

      this.built = true;
      this.maybeAutoOpen();
    },

    /* /cart is reachable from places we don't control — abandoned-cart
       emails, bookmarks, search results, some Shop Pay returns. This store's
       cart template has `main-cart-items` disabled and shows only a
       "use the drawer" message, so without this the page is a dead end
       pointing at a drawer that isn't open. Opening it makes every entry
       point land somewhere usable, and keeps /cart a valid share target.
       The page's own copy stays as the no-JS fallback. */
    maybeAutoOpen: function () {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      // routes.root carries any locale/market prefix, so this holds on
      // /cart, /en-au/cart and anything else Shopify serves it under.
      var cartPath = (root + 'cart').replace(/\/{2,}/g, '/').replace(/\/+$/, '');
      var here = window.location.pathname.replace(/\/+$/, '');
      if (here !== cartPath) return;

      var self = this;
      // Next frame, so the seeded render has painted before the panel slides.
      requestAnimationFrame(function () { self.open(); });
    },

    cacheNodes: function () {
      var r = this.el;
      this.n = {
        scrim: h('[data-ez-scrim]', r),
        panel: h('[data-ez-panel]', r),
        count: h('[data-ez-count]', r),
        ship: h('[data-ez-ship]', r),
        shipMsg: h('[data-ez-ship-msg]', r),
        shipSaved: h('[data-ez-ship-saved]', r),
        shipFill: h('[data-ez-ship-fill]', r),
        body: h('[data-ez-body]', r),
        items: h('[data-ez-items]', r),
        empty: h('[data-ez-empty]', r),
        upsell: h('[data-ez-upsell]', r),
        upsellHead: h('[data-ez-upsell-head]', r),
        upsellList: h('[data-ez-upsell-list]', r),
        foot: h('[data-ez-foot]', r),
        subtotal: h('[data-ez-subtotal]', r),
        saveRow: h('[data-ez-save-row]', r),
        shipRow: h('[data-ez-ship-row]', r),
        save: h('[data-ez-save]', r),
        total: h('[data-ez-total]', r),
        prot: h('[data-ez-prot]', r),
        protSub: h('[data-ez-prot-sub]', r),
        protToggle: h('[data-ez-prot-toggle]', r),
        protExplain: h('[data-ez-prot-explain]', r),
        form: h('[data-ez-form]', r),
        checkoutAmt: h('[data-ez-checkout-amount]', r),
        error: h('[data-ez-error]', r)
      };
    },

    /* ---------------------------------------------------------------- bind */

    bind: function () {
      var self = this;

      /* Artwork thumbnails point at Uploadcare derivatives built from the
         upload's UUID. Most uploads are images and resolve fine, but a PDF
         or an odd file type can 404 the derivative, and a broken-image icon
         where the product photo should be looks like a bug in the cart.
         Capture phase is required: `error` on <img> does not bubble. */
      if (this.n.items) {
        this.n.items.addEventListener('error', function (e) {
          var img = e.target;
          if (!img || img.tagName !== 'IMG') return;
          var fb = img.getAttribute('data-ez-fallback');
          if (!fb) return;
          img.removeAttribute('data-ez-fallback');   // never loop
          img.src = fb;

          /* The artwork could not be shown, so restore the written
             confirmation — otherwise this row claims nothing was uploaded. */
          var row = img.closest ? img.closest('.ez-item') : null;
          var label = row && row.querySelector('[data-ez-art-label]');
          if (label) label.hidden = false;
        }, true);
      }

      /* Click-outside-to-close, via TWO independent paths.
         The scrim listener alone was a single point of failure: it depends on
         the scrim actually being the hit target, which stops being true the
         moment anything changes the stacking order or overlays the panel. The
         host-level check below closes on anything landing outside the panel
         regardless of what the scrim is doing. close() is idempotent, so both
         firing is harmless. */
      if (this.n.scrim) {
        this.n.scrim.addEventListener('click', function () { self.close(); });
      }

      this.el.addEventListener('click', function (e) {
        if (e.target.closest('[data-ez-close]')) { self.close(); return; }

        /* Anything inside the panel is handled below; anything outside it,
           while open, closes the drawer. */
        if (self.el.classList.contains('ez-open') && !e.target.closest('[data-ez-panel]')) {
          self.close();
          return;
        }

        var dec = e.target.closest('[data-ez-dec]');
        var inc = e.target.closest('[data-ez-inc]');
        var rm = e.target.closest('[data-ez-remove]');
        var up = e.target.closest('[data-ez-upsell-add]');
        var upl = e.target.closest('[data-ez-upload]');

        if (upl) { self.uploadForLine(upl.dataset.ezUpload); return; }

        var brm = e.target.closest('[data-ez-bundle-remove]');
        if (brm) { self.removeLines(JSON.parse(brm.dataset.ezBundleRemove)); return; }

        if (dec) self.changeLine(dec.dataset.key, parseInt(dec.dataset.qty, 10) - 1);
        else if (inc) self.changeLine(inc.dataset.key, parseInt(inc.dataset.qty, 10) + 1);
        else if (rm) self.changeLine(rm.dataset.key, 0);
        else if (up) self.addFromUpsell(up);
      });

      if (this.n.protToggle) {
        this.n.protToggle.addEventListener('change', function () {
          self.toggleProtection(this.checked);
        });
      }

      this.el.addEventListener('click', function (e) {
        var q = e.target.closest('[data-ez-prot-info]');
        if (!q || !self.n.protExplain) return;
        var open = self.n.protExplain.hidden;
        self.n.protExplain.hidden = !open;
        q.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.el.classList.contains('ez-open')) self.close();
      });

      /* Checkout. The fast path is a native form POST with no JS in it at
         all. We only intervene when an add is still in flight. */
      this.n.form.addEventListener('submit', function (e) {
        if (self.pendingAdds.size === 0) return; // native submit, fastest path

        e.preventDefault();
        self.clearError();

        Promise.allSettled(Array.from(self.pendingAdds)).then(function (results) {
          var failed = results.some(function (r) { return r.status === 'rejected'; });
          if (failed) {
            // Do not navigate on a failed add — the customer would check out
            // a cart that is missing what they just tapped.
            self.showError('Something went wrong adding that item. Please try again.');
            return;
          }
          self.n.form.submit();
        });
      });
    },

    /* -------------------------------------------------------- open / close */

    open: function () {
      this.lastFocus = document.activeElement;
      this.el.classList.add('ez-open');
      this.el.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';

      var self = this;
      /* Seeded render is already on screen; this only catches changes made
         in another tab or by an app since page render.
         Skipped while an add is in flight: addItems() already refetches when
         it resolves, and firing here too meant every add-to-cart cost THREE
         cart round trips (add + two /cart.js) instead of two. Found by
         benchmarking, not by reading the code. */
      if (this.pendingAdds.size === 0) {
        this.fetchCart().then(function () { self.render(); });
      }

      // Focus the panel, not the close button — see the CSS note. Keeps the
      // dialog keyboard-reachable without painting a ring on the X.
      if (this.n.panel && this.n.panel.focus) {
        try { this.n.panel.focus({ preventScroll: true }); } catch (e) { this.n.panel.focus(); }
      }
    },

    close: function () {
      this.el.classList.remove('ez-open');
      this.el.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    },

    toggle: function () {
      if (this.el.classList.contains('ez-open')) this.close();
      else this.open();
    },

    /* -------------------------------------------------------------- server */

    fetchCart: function () {
      var self = this;
      return fetch(window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root + 'cart.js'
        : '/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          self.cart = cart;
          self.currency = cart.currency || self.currency;
          return cart;
        })
        .catch(function () { return self.cart; });
    },

    /* /cart/change.js only touches a line that already exists, and only by
       line.key. Passing a variant id for something not in the cart returns
       "no valid id or line parameter". Inserts go through /cart/add.js. */
    changeLine: function (key, qty) {
      var self = this;
      if (!key) return;
      qty = Math.max(0, qty || 0);

      this.setLineBusy(key, true);
      this.clearError();

      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.body && res.body.description || 'Cart update failed');
          self.cart = res.body;
          self.currency = res.body.currency || self.currency;
          self.render();
          self.broadcast();
        })
        .catch(function (err) {
          self.showError(err.message || 'Could not update your cart.');
          self.setLineBusy(key, false);
        });
    },

    /* In-drawer artwork upload for a custom tattoo added via quick-add,
       which bypasses the PDP and therefore the uploader.

       Linking back to the PDP cannot work: the product page's only verb is
       "add", so returning with a file produces a SECOND line and leaves the
       empty one behind. Instead we drive UploadKit's own uploader — it wraps
       Uploadcare, so openDialog() gives us a real picker — and write the
       resulting URL onto the existing line.

       The shop's Uploadcare key is read at runtime from UploadKit.config
       rather than hardcoded, so rotating it needs no change here. */
    /* Resolves once Uploadcare is usable, loading it if this page never did.
       Manual-start is forced before the script runs so the freshly-loaded
       library does not scan the DOM and hijack unrelated inputs. The promise
       is memoised: two fast clicks must not inject the script twice. */
    ensureUploader: function () {
      var cfg = window.UploadKit && window.UploadKit.config;
      if (!cfg || !cfg.ucKey) return Promise.reject(new Error('nokey'));
      if (window.uploadcare && typeof window.uploadcare.openDialog === 'function') {
        return Promise.resolve(cfg.ucKey);
      }
      if (this._ucLoading) return this._ucLoading;

      window.UPLOADCARE_PUBLIC_KEY = cfg.ucKey;
      window.UPLOADCARE_MANUAL_START = true;

      this._ucLoading = new Promise(function (resolve, reject) {
        var sc = document.createElement('script');
        sc.src = UC_WIDGET_SRC;
        sc.async = true;
        sc.onload = function () {
          if (window.uploadcare && typeof window.uploadcare.openDialog === 'function') resolve(cfg.ucKey);
          else reject(new Error('loaded-no-dialog'));
        };
        sc.onerror = function () { reject(new Error('load-failed')); };
        document.head.appendChild(sc);
      });
      return this._ucLoading;
    },

    uploadForLine: function (key) {
      var self = this;
      var btn = this.el.querySelector('[data-ez-upload="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]');
      if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }

      var release = function () { if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; } };

      this.ensureUploader().then(function (ucKey) {
        release();
        var uc = window.uploadcare;
        var dialog;
        try {
          dialog = uc.openDialog(null, { publicKey: ucKey, multiple: false, previewStep: true });
        } catch (e) {
          self.showError('Could not open the upload window. Please try the product page.');
          return;
        }
        if (!dialog || typeof dialog.done !== 'function') return;
        self._bindDialog(dialog, key);
      }).catch(function () {
        release();
        self.showError('Uploads aren\'t available right now — please open the product page to add your design.');
      });
    },

    _bindDialog: function (dialog, key) {
      var self = this;

      dialog.done(function (file) {
        if (!file || typeof file.done !== 'function') return;
        file.done(function (info) {
          if (info && info.cdnUrl) self.attachUpload(key, info.cdnUrl);
        });
        if (typeof file.fail === 'function') {
          file.fail(function () { self.showError('That file could not be uploaded. Please try again.'); });
        }
      });
    },

    /* Writes the artwork URL onto an EXISTING line. /cart/change.js accepts
       `properties` and mutates in place — verified live: quantity preserved,
       no duplicate line created. The line KEY changes, because Shopify
       derives it from variant + properties, which is why the response cart
       is adopted wholesale rather than patching the old key. */
    attachUpload: function (key, url) {
      var self = this;
      var line = null;
      (this.cart && this.cart.items || []).forEach(function (li) { if (li.key === key) line = li; });
      if (!line) return;

      var props = {};
      for (var k in (line.properties || {})) {
        if (Object.prototype.hasOwnProperty.call(line.properties, k)) props[k] = line.properties[k];
      }
      props[UPLOAD_PROP] = url;

      this.setLineBusy(key, true);
      this.clearError();

      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: line.quantity, properties: props })
      })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error((res.body && res.body.description) || 'Could not attach your design');
          self.cart = res.body;
          self.currency = res.body.currency || self.currency;
          self.render();
          self.broadcast();
        })
        .catch(function (err) {
          self.showError(err.message || 'Could not attach your design.');
          self.setLineBusy(key, false);
        });
    },

    /* Removes several lines in a single request. /cart/change.js only takes
       one line at a time, so removing a 3-item bundle that way would fire
       three sequential round trips and leave a half-removed bundle visible
       in between if one failed. /cart/update.js accepts a map keyed on line
       key and applies it atomically. */
    removeLines: function (keys) {
      var self = this;
      if (!keys || !keys.length) return;
      var updates = {};
      keys.forEach(function (k) { updates[k] = 0; });

      keys.forEach(function (k) { self.setLineBusy(k, true); });
      this.clearError();

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error((res.body && res.body.description) || 'Could not remove bundle');
          self.cart = res.body;
          self.currency = res.body.currency || self.currency;
          self.render();
          self.broadcast();
        })
        .catch(function (err) {
          self.showError(err.message || 'Could not remove that bundle.');
          keys.forEach(function (k) { self.setLineBusy(k, false); });
        });
    },

    /* Multi-item add. Any secondary item rides along in the SAME request as
       the product — a follow-up request doubles the round trips and can
       leave the cart half-built if the second one fails. If the combined
       call fails we retry with the primary item alone, so a secondary item
       can never block the sale. */
    addItems: function (items, opts) {
      var self = this;
      opts = opts || {};

      var p = fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          if (r.ok) return r.json();
          if (items.length > 1) {
            // Retry primary-only.
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [items[0]] })
            }).then(function (r2) {
              if (!r2.ok) return r2.json().then(function (b) { throw new Error(b.description || 'Add failed'); });
              return r2.json();
            });
          }
          return r.json().then(function (b) { throw new Error(b.description || 'Add failed'); });
        })
        .then(function () { return self.fetchCart(); })
        .then(function () {
          self.render();
          self.broadcast();
        });

      this.pendingAdds.add(p);
      p.catch(function (err) {
        self.showError(err.message || 'Could not add that item.');
      }).finally(function () {
        self.pendingAdds.delete(p);
        // Amount only appears once the server has spoken.
        self.patchCheckoutAmount();
      });

      if (!opts.silent) this.open();
      return p;
    },

    addFromUpsell: function (btn) {
      var id = parseInt(btn.dataset.ezUpsellAdd, 10);
      if (!id) return;
      btn.disabled = true;
      btn.textContent = 'Adding';
      this.addItems([{ id: id, quantity: 1 }], { silent: true });
    },

    protectionLine: function () {
      var c = this.cart;
      if (!c || !c.items || !PROTECTION) return null;
      for (var i = 0; i < c.items.length; i++) {
        if (isProt(c.items[i])) return c.items[i];
      }
      return null;
    },

    /* Auto opt-in. Only ever fires when the cart already holds a real
       product — adding protection to an empty cart would be nonsense — and
       only when the shopper hasn't explicitly opted out this session. */
    ensureProtection: function () {
      if (!PROTECTION || !this.cart) return;

      var line = this.protectionLine();
      var hasRealItem = (this.cart.items || []).some(function (li) { return !isProt(li); });

      /* Orphan guard. Protection is filtered out of the item list and the
         header count, so if the shopper removes their last real product the
         cart would render as EMPTY while still holding a $2.99 line they
         cannot see or reach — and the footer, including Checkout, is hidden
         in the empty state. Never leave it in the cart on its own. */
      if (line && !hasRealItem) {
        this.changeLine(line.key, 0);
        return;
      }

      if (!protOptIn()) return;
      if (line) return;
      if (!hasRealItem) return;

      this.addItems([{ id: PROTECTION.id, quantity: 1 }], { silent: true });
    },

    toggleProtection: function (on) {
      setProtOptIn(on);
      var line = this.protectionLine();

      if (on) {
        if (!line) this.addItems([{ id: PROTECTION.id, quantity: 1 }], { silent: true });
        return;
      }
      // /cart/change.js only accepts a line that already exists, keyed on
      // line.key — a variant id here returns "no valid id or line parameter".
      if (line) this.changeLine(line.key, 0);
    },

    renderProtection: function (c, cur) {
      if (!this.n.prot) return;
      if (!PROTECTION) { this.n.prot.hidden = true; return; }

      var hasRealItem = (c.items || []).some(function (li) { return !isProt(li); });
      this.n.prot.hidden = !hasRealItem;
      if (!hasRealItem) return;

      if (this.n.protSub) {
        this.n.protSub.textContent =
          'Covers loss, theft and damage in transit. ' + formatMoney(PROTECTION.price, cur);
      }
      if (this.n.protToggle) this.n.protToggle.checked = !!this.protectionLine();
    },

    broadcast: function () {
      document.dispatchEvent(new CustomEvent('ez:cart:updated', { detail: this.cart }));
      // Keep Dawn's own subscribers working if anything is listening.
      if (window.PUB_SUB_EVENTS && window.publish) {
        try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { cart: this.cart }); } catch (e) {}
      }
      this.syncBubble();
    },

    /* Header bubble. The old module only set innerHTML and never toggled the
       hidden/flex classes, so a cart going 0 -> 1 left the bubble invisible. */
    syncBubble: function () {
      var n = this.cart
        ? (this.cart.items || []).reduce(function (t, li) { return isProt(li) ? t : t + li.quantity; }, 0)
        : 0;
      var bubble = document.getElementById('CartCount');
      if (!bubble) return;
      bubble.innerHTML = n;
      bubble.classList.toggle('hidden', n === 0);
      bubble.classList.toggle('flex', n > 0);
    },

    /* -------------------------------------------------------------- render */

    render: function () {
      if (!this.cart) return;
      var c = this.cart;
      var cur = this.currency;

      /* Protection is a service line, not something the shopper "added" —
         it is excluded from the header count and from the item list, and
         surfaced only as its own toggle row. */
      var shownCount = (c.items || []).reduce(function (n, li) {
        return isProt(li) ? n : n + li.quantity;
      }, 0);
      this.n.count.textContent = '(' + shownCount + (shownCount === 1 ? ' ITEM)' : ' ITEMS)');

      this.renderShipping(c, cur);
      this.renderItems(c, cur);
      this.renderFooter(c, cur);
      this.fetchCompareAt();
      this.renderProtection(c, cur);
      this.ensureProtection();
      this.refreshRecommendations();
      this.renderUpsells(c, cur);
      this.syncBubble();
    },

    /* total_price is post-discount, which is what Shopify's shipping rules
       actually test. Summing line prices instead would tell a discounted
       cart it had free shipping and then charge for it at checkout. */
    renderShipping: function (c, cur) {
      var total = c.total_price || 0;
      var remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - total);

      var pct = (total / FREE_SHIPPING_THRESHOLD) * 100;
      // Guard against a NaN/Infinity ever reaching the style property: an
      // invalid width string is silently dropped by the browser and the bar
      // just sits at zero with no error anywhere.
      if (!isFinite(pct) || pct < 0) pct = 0;
      if (pct > 100) pct = 100;

      if (this.n.shipFill) {
        this.n.shipFill.style.width = pct.toFixed(2) + '%';
      }

      if (remaining === 0) {
        this.n.ship.classList.add('is-unlocked');
        this.n.shipMsg.innerHTML = '<strong>Free shipping unlocked</strong>';
        if (this.n.shipSaved) {
          var saving = STANDARD_SHIPPING[cur] || 0;
          if (saving > 0) {
            this.n.shipSaved.textContent = formatMoney(saving, cur) + ' saved';
            this.n.shipSaved.hidden = false;
          } else {
            this.n.shipSaved.hidden = true;
          }
        }
      } else {
        this.n.ship.classList.remove('is-unlocked');
        this.n.shipMsg.innerHTML =
          "You're <span class=\"ez-ship__amt\">" + esc(formatMoney(remaining, cur)) +
          '</span> away from <strong>free shipping</strong>';
        if (this.n.shipSaved) this.n.shipSaved.hidden = true;
      }
    },

    renderItems: function (c, cur) {
      var self = this;
      var shown = (c.items || []).filter(function (li) { return !isProt(li); });
      var isEmpty = shown.length === 0;
      this.n.empty.hidden = !isEmpty;
      this.n.items.hidden = isEmpty;
      this.n.foot.hidden = isEmpty;

      if (isEmpty) { this.n.items.innerHTML = ''; return; }

      /* Group Recharge bundle-builder lines.
         The builder writes _rc_bundle (a per-bundle instance id) onto every
         member line, plus _rc_bundle_parent (the parent product HANDLE).
         Ungrouped, a 3-sleeve bundle reads as three unrelated products at
         three prices, which is both confusing and makes the bundle discount
         invisible. Grouping key is _rc_bundle alone — _rc_bundle_parent is
         a payload, not an identity, and two different bundles can share it.
         Order is preserved by the position of each bundle's FIRST member. */
      var rows = [];
      var seenBundle = {};
      shown.forEach(function (li) {
        var bid = li.properties && li.properties._rc_bundle;
        if (!bid) { rows.push({ bundle: false, li: li }); return; }
        if (seenBundle[bid]) { seenBundle[bid].lines.push(li); return; }
        seenBundle[bid] = {
          bundle: true,
          id: bid,
          parent: li.properties._rc_bundle_parent || '',
          lines: [li]
        };
        rows.push(seenBundle[bid]);
      });

      var html = rows.map(function (row) {
        if (!row.bundle) return renderLine(row.li);
        return renderBundle(row);
      }).join('');

      function renderBundle(b) {
        // Price is SUMMED from the members. The parent product is $0 — it
        // exists to carry a title and image, not a price.
        var total = 0, plan = null, bundleUnits = 0;
        b.lines.forEach(function (li) {
          total += li.final_line_price;
          bundleUnits += li.quantity;
          if (!plan && li.selling_plan_allocation && li.selling_plan_allocation.selling_plan) {
            plan = li.selling_plan_allocation.selling_plan.name;
          }
        });

        var meta = self.productMeta[b.parent] || null;
        var title = (meta && meta.title) || 'Bundle';
        var image = (meta && meta.image) || b.lines[0].image;
        var url = (meta && meta.url) || b.lines[0].url;

        var contents = b.lines.map(function (li) {
          return esc(li.quantity + '× ' + li.product_title);
        }).join('<br>');

        var keys = b.lines.map(function (li) { return li.key; });

        return '' +
          '<li class="ez-item ez-item--bundle" data-ez-bundle="' + esc(b.id) + '">' +
            '<div class="ez-item__media">' +
              (image ? '<img src="' + esc(sized(image, 200)) + '" alt="' + esc(title) + '" width="76" height="76">' : '') +
            '</div>' +
            '<div class="ez-item__info">' +
              '<a class="ez-item__title" href="' + esc(url) + '">' + esc(title) + '</a>' +
              (plan ? '<div class="ez-item__plan">' + esc(plan) + '</div>' : '') +
              '<div class="ez-bundle__contents">' + contents + '</div>' +
              '<div class="ez-item__foot">' +
                // No stepper. Bundle quantity is fixed by the builder; the
                // only sane cart-side action is removing the whole thing.
                '<span class="ez-bundle__count">' + bundleUnits + (bundleUnits === 1 ? ' item' : ' items') + '</span>' +
                '<button type="button" class="ez-item__remove" data-ez-bundle-remove=\'' +
                  esc(JSON.stringify(keys)) + '\'>Remove</button>' +
                '<div class="ez-item__price">' + esc(formatMoney(total, cur)) + '</div>' +
              '</div>' +
            '</div>' +
          '</li>';
      }

      /* True when a custom-tattoo line has no uploaded artwork yet. The
         uploader itself lives on the product page (UploadKit renders it
         there), so the drawer can only prompt and link — it cannot host the
         file picker. Detected on the absence of any URL-valued property
         rather than on a specific key name, since the key is merchant-set
         ("Uploaded file") and could be renamed in the app. */
      function needsUpload(li) {
        if (!li.handle || li.handle.indexOf('custom-') !== 0) return false;
        var props = li.properties || {};
        for (var k in props) {
          if (!props.hasOwnProperty(k)) continue;
          if (k.charAt(0) === '_') continue;
          if (/^(https?:\/\/|\/\/|\/)\S+$/i.test(String(props[k]).trim())) return false;
        }
        return true;
      }

      function renderLine(li) {
        var art = artworkThumb(li);
        var props = '';
        if (li.properties) {
          var rows = [];
          for (var k in li.properties) {
            if (!li.properties.hasOwnProperty(k)) continue;
            if (k.charAt(0) === '_') continue;          // internal props stay hidden
            var v = li.properties[k];
            if (v === null || v === '') continue;

            /* Customer file uploads (UploadKit, custom tattoos) arrive as a
               URL. We deliberately do NOT render that URL, and do NOT rely on
               UploadKit's thumbnail swap.

               History: an earlier revision emitted the URL verbatim so that
               UploadKit's app embed — which scans the rendered DOM for raw
               URL text and replaces it in place with a thumbnail — could find
               it. That worked on paper and was wrong twice over. It flickered
               the raw URL on every drawer open while waiting for the swap,
               and it never fired at all for files uploaded from inside the
               drawer, because the embed only scans on page load.

               A confirmation label owns its own rendering, is stable across
               opens, and behaves identically whether the file came from the
               PDP or from the drawer. */
            var isUrl = /^(https?:\/\/|\/\/|\/)\S+$/i.test(String(v).trim());
            if (isUrl) {
              /* The label is ALWAYS emitted, and merely hidden while the
                 artwork is on screen — the thumbnail says "your design is
                 attached" better than any text can.

                 It is not simply omitted, because a thumbnail that 404s falls
                 back to the product photo, and omitting the label there left
                 the row with NO confirmation at all: a custom tattoo that
                 looked like nothing had been uploaded. Keeping the node in the
                 DOM lets the error handler reveal it, so a broken thumbnail
                 degrades to the tick design instead of to silence. */
              rows.push(
                '<span class="ez-item__props--file" data-ez-art-label' +
                (art ? ' hidden' : '') + '>Artwork attached</span>'
              );
            } else {
              rows.push(esc(k) + ': ' + esc(v));
            }
          }
          if (rows.length) props = '<div class="ez-item__props">' + rows.join('<br>') + '</div>';
        }

        var plan = li.selling_plan_allocation && li.selling_plan_allocation.selling_plan
          ? '<div class="ez-item__plan">' + esc(li.selling_plan_allocation.selling_plan.name) + '</div>'
          : '';

        var variant = (li.variant_title && li.variant_title !== 'Default Title')
          ? '<div class="ez-item__variant">' + esc(li.variant_title) + '</div>' : '';

        /* Prices come straight from the server. Nothing here is computed:
           automatic and bundle discounts apply at cart level, so any
           optimistic local maths renders full price and then visibly
           corrects itself a moment later. */
        var priceHtml;
        var ca = self.compareAtFor(li);
        if (ca) {
          // On sale: strike the compare-at, show what they actually pay.
          priceHtml = '<del>' + esc(formatMoney(ca * li.quantity, cur)) + '</del>' +
                      '<ins>' + esc(formatMoney(li.final_line_price, cur)) + '</ins>';
        } else if (li.original_line_price > li.final_line_price) {
          // Cart- or line-level discount rather than a sale price.
          priceHtml = '<del>' + esc(formatMoney(li.original_line_price, cur)) + '</del>' +
                      '<ins>' + esc(formatMoney(li.final_line_price, cur)) + '</ins>';
        } else {
          priceHtml = esc(formatMoney(li.final_line_price, cur));
        }

        return '' +
          '<li class="ez-item" data-ez-line="' + esc(li.key) + '">' +
            (needsUpload(li)
              ? '<button type="button" class="ez-item__upload" data-ez-upload="' + esc(li.key) + '">' +
                  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" ' +
                  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
                  '<path d="M12 16V4M12 4 7.5 8.5M12 4l4.5 4.5"/><path d="M4 16v2.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V16"/>' +
                  '</svg><span>Upload<br>design</span></button>'
              : '<div class="ez-item__media">' +
                  /* NO loading="lazy" on line images. The panel is
                     translate3d'd fully off-viewport until the drawer opens,
                     so the browser never schedules a lazy fetch — the image
                     stays blank AND never errors, which also stranded the
                     fallback below. Measured: complete=false indefinitely,
                     while the identical src loaded fine in a detached Image.
                     A cart drawer holds a handful of images the customer is
                     about to look at; lazy buys nothing here. */
                  (art
                    /* data-ez-fallback rather than an inline onerror: inline
                       handlers are blocked outright under a strict CSP, and
                       a non-image upload (a PDF, say) must degrade to the
                       product photo rather than a broken-image icon. */
                    ? '<img class="ez-item__art" src="' + esc(art) + '" alt="Your uploaded artwork" width="76" height="76"' +
                      (li.image ? ' data-ez-fallback="' + esc(sized(li.image, 200)) + '"' : '') + '>'
                    : (li.image ? '<img src="' + esc(sized(li.image, 200)) + '" alt="' + esc(li.product_title) + '" width="76" height="76">' : '')) +
                '</div>') +
            '<div class="ez-item__info">' +
              '<a class="ez-item__title" href="' + esc(li.url) + '">' + esc(li.product_title) + '</a>' +
              variant + plan + props +
              '<div class="ez-item__foot">' +
                '<div class="ez-qty">' +
                  '<button type="button" class="ez-qty__btn" data-ez-dec data-key="' + esc(li.key) + '" data-qty="' + li.quantity + '" aria-label="Decrease quantity">&minus;</button>' +
                  '<span class="ez-qty__val">' + li.quantity + '</span>' +
                  '<button type="button" class="ez-qty__btn" data-ez-inc data-key="' + esc(li.key) + '" data-qty="' + li.quantity + '" aria-label="Increase quantity">+</button>' +
                '</div>' +
                '<button type="button" class="ez-item__remove" data-ez-remove data-key="' + esc(li.key) + '">Remove</button>' +
                '<div class="ez-item__price">' + priceHtml + '</div>' +
              '</div>' +
            '</div>' +
          '</li>';
      }

      this.n.items.innerHTML = html;
    },

    /* The footer is built once in Liquid and only ever patched here. Blowing
       it away with innerHTML would replace the Checkout button node, and a
       node swapped between pointerdown and click makes the browser retarget
       the click to a parent with no handler — the tap vanishes silently. */
    renderFooter: function (c, cur) {
      // Subtotal row was removed from the markup; guard rather than assume.
      if (this.n.subtotal) this.n.subtotal.textContent = formatMoney(c.items_subtotal_price, cur);
      this.n.total.textContent = formatMoney(c.total_price, cur);

      var self = this;

      /* Three independent components, none of them double-counted:
         - compare-at: sale pricing, "was" vs "now"
         - discounts: cart/line level, applied on top of the sale price
         - shipping: the standard rate actually avoided in THIS market */
      var compareSaving = (c.items || []).reduce(function (t, li) {
        var ca = self.compareAtFor(li);
        return ca ? t + (ca - li.price) * li.quantity : t;
      }, 0);

      var saved = compareSaving + ((c.original_total_price || 0) - (c.total_price || 0));
      /* ONE threshold test, used by both the saving figure and the Shipping
         row. Testing it twice invites the two disagreeing — a cart showing
         "Shipping FREE" while the saving omits the shipping component would
         be worse than either alone. Works in every market because the
         threshold is expressed in the customer's own presentment currency. */
      var freeShip = (c.total_price || 0) >= FREE_SHIPPING_THRESHOLD;
      var shipSaving = freeShip ? (STANDARD_SHIPPING[cur] || 0) : 0;

      if (this.n.shipRow) this.n.shipRow.hidden = !freeShip;
      var totalSaved = saved + shipSaving;

      if (totalSaved > 0) {
        this.n.saveRow.hidden = false;
        this.n.save.textContent = '-' + formatMoney(totalSaved, cur);
      } else {
        this.n.saveRow.hidden = true;
      }

      this.patchCheckoutAmount();
    },

    patchCheckoutAmount: function () {
      if (!this.n.checkoutAmt) return;
      // Never show an amount while an add is unresolved — it would be stale.
      if (this.pendingAdds.size > 0 || !this.cart) {
        this.n.checkoutAmt.hidden = true;
        return;
      }
      this.n.checkoutAmt.textContent = ' · ' + formatMoney(this.cart.total_price, this.currency);
      this.n.checkoutAmt.hidden = false;
    },

    /* Cart-driven recommendations.
       Shopify's Product Recommendations API returns items related to what is
       already in the cart, in the customer's own market and presentment
       currency — which is strictly better than a fixed Liquid-seeded pool
       both for relevance and because it can't go stale per market.
       The Liquid pool stays as the fallback for when the API returns nothing
       (new products with no recommendation data yet), so the row degrades
       rather than disappearing. */
    refreshRecommendations: function () {
      var self = this;
      var c = this.cart;
      if (!c || !c.items || !c.items.length) { this.recPool = []; return; }

      var anchor = c.items[0].product_id;
      if (this.recAnchor === anchor) return; // already fetched for this cart
      this.recAnchor = anchor;

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var url = root + 'recommendations/products.json?product_id=' + anchor +
                '&limit=10&intent=related';

      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var list = (d && d.products) || [];
          self.recPool = list.map(function (p) {
            var v = (p.variants || []).filter(function (x) { return x.available; })[0];
            if (!v) return null;
            return {
              id: v.id,
              title: p.title,
              price: v.price,               // presentment minor units, same as cart.js
              image: p.featured_image || (p.images && p.images[0]) || null,
              available: true
            };
          }).filter(Boolean);
          // Re-render just the upsell row now that we have better data.
          if (self.cart) self.renderUpsells(self.cart, self.currency);
        })
        .catch(function () { self.recPool = []; });
    },

    /* Both numbers here come from Shopify — nothing is derived from a
       client-side guess at what a discount ought to be. The compare-at only
       appears once the fetch resolves; until then the line shows the plain
       server price. So the row is additive and never renders a wrong figure
       that then visibly corrects itself. */
    fetchCompareAt: function () {
      var self = this, c = this.cart, cur = this.currency;
      if (!c || !c.items) return;

      var need = [];
      c.items.forEach(function (li) {
        if (isProt(li) || !li.handle) return;
        var k = li.handle + '|' + cur;
        if (!(k in self.priceBook)) { self.priceBook[k] = null; need.push(li.handle); }
      });

      /* Bundle parents are not line items, so they never appear in the loop
         above — collect their handles separately or the grouped row has no
         title or image. */
      var parents = [];
      c.items.forEach(function (li) {
        var ph = li.properties && li.properties._rc_bundle_parent;
        if (ph && !self.productMeta[ph] && parents.indexOf(ph) === -1) parents.push(ph);
      });
      need = need.concat(parents);

      if (!need.length) return;

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      Promise.all(need.map(function (hd) {
        return fetch(root + 'products/' + hd + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (pr) {
            var map = {};
            if (pr && pr.variants) {
              pr.variants.forEach(function (v) { map[v.id] = v.compare_at_price || 0; });
            }
            self.priceBook[hd + '|' + cur] = map;
            if (pr) {
              self.productMeta[hd] = {
                title: pr.title,
                image: pr.featured_image || (pr.images && pr.images[0]) || null,
                url: '/products/' + hd
              };
            }
          })
          .catch(function () { self.priceBook[hd + '|' + cur] = {}; });
      })).then(function () {
        if (!self.cart) return;
        self.renderItems(self.cart, self.currency);
        self.renderFooter(self.cart, self.currency);
      });
    },

    /* Per-unit compare-at, or 0 when the item isn't on sale. */
    compareAtFor: function (li) {
      if (!li || !li.handle) return 0;
      var map = this.priceBook[li.handle + '|' + this.currency];
      if (!map) return 0;
      var ca = map[li.variant_id] || 0;
      return ca > li.price ? ca : 0;
    },

    renderUpsells: function (c, cur) {
      /* Prefer live, cart-aware recommendations; fall back to the Liquid pool. */
      var pool = (this.recPool && this.recPool.length)
        ? this.recPool
        : (window.__EZ_UPSELLS__ || []);
      var remaining = FREE_SHIPPING_THRESHOLD - (c.total_price || 0);

      /* The row stays visible once the threshold is met — only the framing
         changes. Removing it at the moment the cart crosses 65 pulls the
         highest-intent merchandising slot off screen exactly when the
         customer has proven they'll spend. */
      if (!pool.length || !c.items || !c.items.length) {
        this.n.upsell.hidden = true;
        return;
      }

      var inCart = {};
      c.items.forEach(function (li) { inCart[li.variant_id] = true; });

      var available = pool.filter(function (p) { return !inCart[p.id] && p.available; });

      var candidates;
      if (remaining > 0) {
        // Below the threshold, rank by how well each item closes the gap.
        candidates = available.sort(function (a, b) {
          var ac = a.price >= remaining ? 0 : 1;
          var bc = b.price >= remaining ? 0 : 1;
          if (ac !== bc) return ac - bc;
          return Math.abs(a.price - remaining) - Math.abs(b.price - remaining);
        }).slice(0, 3);
      } else {
        // Above it there is no gap to close, so keep the recommendation
        // API's own relevance order rather than imposing a price sort.
        candidates = available.slice(0, 3);
      }

      if (!candidates.length) { this.n.upsell.hidden = true; return; }

      this.n.upsellHead.textContent = remaining > 0
        ? 'Add ' + formatMoney(remaining, cur) + ' and shipping is on us'
        : 'Frequently added';

      this.n.upsellList.innerHTML = candidates.map(function (p) {
        return '' +
          '<li class="ez-upsell__item">' +
            '<div class="ez-upsell__media">' +
              (p.image ? '<img src="' + esc(sized(p.image, 140)) + '" alt="" width="52" height="52">' : '') +
            '</div>' +
            '<div>' +
              '<p class="ez-upsell__title">' + esc(p.title) + '</p>' +
              '<span class="ez-upsell__price">' + esc(formatMoney(p.price, cur)) + '</span>' +
            '</div>' +
            '<button type="button" class="ez-upsell__add" data-ez-upsell-add="' + p.id + '">Add</button>' +
          '</li>';
      }).join('');

      this.n.upsell.hidden = false;
    },

    /* ------------------------------------------------------------- ui bits */

    setLineBusy: function (key, busy) {
      var row = this.el.querySelector('[data-ez-line="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]');
      if (!row) return;
      row.classList.toggle('is-busy', !!busy);
      // Lock only the controls that mutate the cart. Checkout is untouched.
      row.querySelectorAll('button').forEach(function (b) { b.disabled = !!busy; });
    },

    showError: function (msg) {
      if (!this.n.error) return;
      this.n.error.textContent = msg;
      this.n.error.hidden = false;
    },

    clearError: function () {
      if (!this.n.error) return;
      this.n.error.hidden = true;
      this.n.error.textContent = '';
    }
  };

  window.EZCart = EZCart;

  /* ----------------------------------------------------------------------
     Add-to-cart interception.
     Registered HERE, at script-execution time, not inside DOMContentLoaded.
     Capture-phase listeners fire in registration order, so anything that
     needs to pre-empt another listener has to be registered while the
     script body runs — deferring to a ready event puts us behind whatever
     registered first.
     ---------------------------------------------------------------------- */

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;

    e.preventDefault();
    e.stopPropagation();

    var fd = new FormData(form);
    var id = fd.get('id');
    if (!id) return;

    var item = { id: parseInt(id, 10), quantity: parseInt(fd.get('quantity') || '1', 10) };

    var sellingPlan = fd.get('selling_plan');
    if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);

    var props = {};
    fd.forEach(function (v, k) {
      var m = k.match(/^properties\[(.+)\]$/);
      if (m) props[m[1]] = v;
    });
    if (Object.keys(props).length) item.properties = props;

    /* Fold protection into THIS request rather than firing a follow-up.
       A second round trip both doubles the latency and can leave the cart
       half-built if it fails. addItems() already retries with the primary
       item alone if the combined call is rejected, so protection can never
       block the sale. */
    var batch = [item];
    if (PROTECTION && protOptIn() && !EZCart.protectionLine()) {
      batch.push({ id: PROTECTION.id, quantity: 1 });
    }

    /* Open on the same tick as the tap. The drawer paints with the cart it
       already has and a bare "Checkout" button, so the button is tappable
       immediately instead of waiting on the network. The amount arrives
       when the server responds. */
    EZCart.addItems(batch);
  }, true);

  /* Header cart button. Also registered at script-execution time so it
     beats any leftover handler that might still be bound. */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-ez-open], #cart-icon-bubble, cart-count, a[href$="/cart"]');
    if (!trigger) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.which === 2) return; // let new-tab through
    e.preventDefault();
    e.stopPropagation();
    EZCart.open();
  }, true);

  /* Back-compat shims so anything still calling the old UpCart globals
     keeps working rather than throwing. */
  window.upcartOpenCart = function () { EZCart.open(); };
  window.upcartModifyCart = function (cart) {
    if (cart) EZCart.cart = cart;
    EZCart.syncBubble();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { EZCart.init(); });
  } else {
    EZCart.init();
  }
})();
